import {
  type CandleWsEvent,
  type ClearinghouseStateResponse,
  SubscriptionClient,
  WebSocketTransport,
} from "@nktkas/hyperliquid"
import { isHLTestnet } from "@src/features/trade/hyperliquid/constants/config"
import {
  MAIN_DEX,
  TRADE_XYZ_DEX,
} from "@src/features/trade/hyperliquid/marketIdentity"
import type {
  HLAccountState,
  HLDexName,
} from "@src/features/trade/hyperliquid/types"
import { logger } from "@src/utils/logger"
import { mapAccountState } from "./hyperliquidService"

// Subscribe promises reject with "WebSocket connection closed" when the
// socket is destroyed mid-handshake (Strict-Mode cleanup). That's expected.
function swallowExpectedClose(operation: string) {
  return (e: unknown) => {
    const message = String(e)
    if (message.includes("WebSocket connection closed")) return
    logger.error(`HL WebSocket ${operation} failed`, { error: message })
  }
}

export function createHyperliquidWebSocket(
  onConnectionChange?: (connected: boolean) => void
) {
  const transport = new WebSocketTransport({ isTestnet: isHLTestnet() })
  const subs = new SubscriptionClient({ transport })
  const handleOpen = () => onConnectionChange?.(true)
  const handleClose = () => onConnectionChange?.(false)
  transport.socket?.addEventListener?.("open", handleOpen)
  transport.socket?.addEventListener?.("close", handleClose)
  transport.socket?.addEventListener?.("error", handleClose)

  return {
    subscribePrices(
      cb: (mids: Record<string, string>, sourceDex: HLDexName) => void
    ) {
      let active = true
      const midsByDex = new Map<HLDexName, Record<string, string>>()
      const emit = (dex: HLDexName, mids: Record<string, string>) => {
        midsByDex.set(dex, mids)
        cb(Object.assign({}, ...midsByDex.values()), dex)
      }
      const subscriptions = [
        subs
          .allMids((event) => {
            if (active) emit(MAIN_DEX, event.mids)
          })
          .catch(swallowExpectedClose("subscribePrices")),
        subs
          .allMids({ dex: TRADE_XYZ_DEX }, (event) => {
            if (active) emit(TRADE_XYZ_DEX, event.mids)
          })
          .catch(swallowExpectedClose("subscribePricesTradeXyz")),
      ]
      return () => {
        active = false
        for (const subscription of subscriptions) {
          void subscription.then((sub) => {
            if (!sub) return
            return sub
              .unsubscribe()
              .catch(swallowExpectedClose("unsubscribePrices"))
          })
        }
      }
    },
    subscribeCandle(
      coin: string,
      interval: "1m" | "3m" | "5m" | "15m" | "1h" | "4h" | "8h" | "1d" | "1w",
      cb: (candle: CandleWsEvent) => void
    ) {
      let active = true
      const subscription = subs
        .candle({ coin, interval }, (event) => {
          if (active) cb(event)
        })
        .catch(swallowExpectedClose("subscribeCandle"))

      return () => {
        active = false
        void subscription.then((sub) => {
          if (!sub) return
          return sub
            .unsubscribe()
            .catch(swallowExpectedClose("unsubscribeCandle"))
        })
      }
    },
    subscribeAccountState(
      user: string,
      dex: HLDexName,
      cb: (state: HLAccountState) => void
    ) {
      let active = true
      const params = dex === MAIN_DEX ? { user } : { user, dex }
      const subscription = subs
        .clearinghouseState(params, (event) => {
          if (active) {
            cb(
              mapAccountState(
                event.clearinghouseState as ClearinghouseStateResponse
              )
            )
          }
        })
        .catch(swallowExpectedClose("subscribeAccountState"))
      return () => {
        active = false
        void subscription.then((sub) => {
          if (!sub) return
          return sub
            .unsubscribe()
            .catch(swallowExpectedClose("unsubscribeAccountState"))
        })
      }
    },
    destroy() {
      transport.socket?.removeEventListener?.("open", handleOpen)
      transport.socket?.removeEventListener?.("close", handleClose)
      transport.socket?.removeEventListener?.("error", handleClose)
      transport.close().catch(swallowExpectedClose("close"))
    },
  }
}

type HyperliquidWebSocket = ReturnType<typeof createHyperliquidWebSocket>

let sharedSocket: HyperliquidWebSocket | null = null
let activeSubscriptionRefs = 0
let connectionIsLive = false
const connectionListeners = new Set<(connected: boolean) => void>()
const subscriptionHealth = createSubscriptionHealthRegistry()

const midsHealthKey = (dex?: HLDexName) =>
  dex == null ? "mids:all" : `mids:${dex || "main"}`
const accountHealthKey = (user: string, dex: HLDexName) =>
  `account:${user}:${dex}`
const candleHealthKey = (coin: string, interval: CandleInterval) =>
  `candle:${coin}:${interval}`

function updateConnectionStatus(connected: boolean) {
  if (!connected) subscriptionHealth.clear()
  if (connectionIsLive === connected) return
  connectionIsLive = connected
  for (const listener of connectionListeners) listener(connected)
}

const midsListeners = new Set<(mids: Record<string, string>) => void>()
let unsubscribeMids: (() => void) | null = null

export type CandleInterval =
  | "1m"
  | "3m"
  | "5m"
  | "15m"
  | "1h"
  | "4h"
  | "8h"
  | "1d"
  | "1w"
type CandleListener = (candle: CandleWsEvent) => void

const candleSubscriptions = new Map<
  string,
  {
    listeners: Set<CandleListener>
    unsubscribe: () => void
  }
>()

const accountListeners = new Map<
  string,
  {
    listeners: Set<(state: HLAccountState) => void>
    unsubscribe: () => void
  }
>()

function retainSocket() {
  if (sharedSocket == null) {
    const nextSocket = createHyperliquidWebSocket((connected) => {
      if (sharedSocket === nextSocket) updateConnectionStatus(connected)
    })
    sharedSocket = nextSocket
  }
  activeSubscriptionRefs += 1
  return sharedSocket
}

function releaseSocket() {
  activeSubscriptionRefs = Math.max(0, activeSubscriptionRefs - 1)
  if (activeSubscriptionRefs === 0) {
    const socket = sharedSocket
    sharedSocket = null
    updateConnectionStatus(false)
    socket?.destroy()
  }
}

export function subscribeHyperliquidConnectionStatus(
  listener: (connected: boolean) => void
) {
  connectionListeners.add(listener)
  return () => {
    connectionListeners.delete(listener)
  }
}

export function getHyperliquidConnectionStatus() {
  return connectionIsLive
}

export function subscribeHyperliquidAccountConnectionStatus(
  user: string,
  dex: HLDexName,
  listener: (connected: boolean) => void
) {
  return subscriptionHealth.subscribe(accountHealthKey(user, dex), listener)
}

export function getHyperliquidAccountConnectionStatus(
  user: string,
  dex: HLDexName
) {
  return subscriptionHealth.get(accountHealthKey(user, dex))
}

export function subscribeHyperliquidMidsConnectionStatus(
  dex: HLDexName | undefined,
  listener: (connected: boolean) => void
) {
  return subscriptionHealth.subscribe(midsHealthKey(dex), listener)
}

export function getHyperliquidMidsConnectionStatus(dex?: HLDexName) {
  return subscriptionHealth.get(midsHealthKey(dex))
}

export function subscribeHyperliquidCandleConnectionStatus(
  coin: string,
  interval: CandleInterval,
  listener: (connected: boolean) => void
) {
  return subscriptionHealth.subscribe(
    candleHealthKey(coin, interval),
    listener
  )
}

export function getHyperliquidCandleConnectionStatus(
  coin: string,
  interval: CandleInterval
) {
  return subscriptionHealth.get(candleHealthKey(coin, interval))
}

export function subscribeHyperliquidMids(
  listener: (mids: Record<string, string>) => void
) {
  const socket = retainSocket()
  midsListeners.add(listener)

  if (unsubscribeMids == null) {
    unsubscribeMids = socket.subscribePrices((mids, sourceDex) => {
      subscriptionHealth.update(midsHealthKey(sourceDex), true)
      subscriptionHealth.update(
        midsHealthKey(),
        subscriptionHealth.get(midsHealthKey(MAIN_DEX)) &&
          subscriptionHealth.get(midsHealthKey(TRADE_XYZ_DEX))
      )
      for (const cb of midsListeners) {
        cb(mids)
      }
    })
  }

  return () => {
    midsListeners.delete(listener)
    if (midsListeners.size === 0) {
      unsubscribeMids?.()
      unsubscribeMids = null
      subscriptionHealth.update(midsHealthKey(MAIN_DEX), false)
      subscriptionHealth.update(midsHealthKey(TRADE_XYZ_DEX), false)
      subscriptionHealth.update(midsHealthKey(), false)
    }
    releaseSocket()
  }
}

export function subscribeHyperliquidCandle(
  coin: string,
  interval: CandleInterval,
  listener: CandleListener
) {
  const socket = retainSocket()
  const key = `${coin}:${interval}`
  let entry = candleSubscriptions.get(key)
  let active = true

  if (entry == null) {
    const listeners = new Set<CandleListener>()
    entry = {
      listeners,
      unsubscribe: socket.subscribeCandle(coin, interval, (candle) => {
        subscriptionHealth.update(candleHealthKey(coin, interval), true)
        for (const cb of listeners) cb(candle)
      }),
    }
    candleSubscriptions.set(key, entry)
  }

  entry.listeners.add(listener)

  return () => {
    if (!active) return
    active = false
    const current = candleSubscriptions.get(key)
    current?.listeners.delete(listener)
    if (current?.listeners.size === 0) {
      current.unsubscribe()
      candleSubscriptions.delete(key)
      subscriptionHealth.update(candleHealthKey(coin, interval), false)
    }
    releaseSocket()
  }
}

export function subscribeHyperliquidAccountState(
  user: string,
  listener: (state: HLAccountState) => void,
  dex: HLDexName = MAIN_DEX
) {
  const socket = retainSocket()
  const key = `${user}:${dex}`
  let entry = accountListeners.get(key)

  if (entry == null) {
    const listeners = new Set<(state: HLAccountState) => void>()
    entry = {
      listeners,
      unsubscribe: socket.subscribeAccountState(user, dex, (state) => {
        subscriptionHealth.update(accountHealthKey(user, dex), true)
        for (const cb of listeners) {
          cb(state)
        }
      }),
    }
    accountListeners.set(key, entry)
  }

  entry.listeners.add(listener)

  return () => {
    const current = accountListeners.get(key)
    current?.listeners.delete(listener)
    if (current != null && current.listeners.size === 0) {
      current.unsubscribe()
      accountListeners.delete(key)
      subscriptionHealth.update(accountHealthKey(user, dex), false)
    }
    releaseSocket()
  }
}

export async function resetSharedHyperliquidWebSocketForTest() {
  unsubscribeMids?.()
  unsubscribeMids = null
  midsListeners.clear()
  for (const entry of accountListeners.values()) {
    entry.unsubscribe()
  }
  accountListeners.clear()
  for (const entry of candleSubscriptions.values()) {
    entry.unsubscribe()
  }
  candleSubscriptions.clear()
  activeSubscriptionRefs = 0
  sharedSocket?.destroy()
  sharedSocket = null
  updateConnectionStatus(false)
  connectionListeners.clear()
  subscriptionHealth.reset()
  await Promise.resolve()
}

function createSubscriptionHealthRegistry() {
  const liveKeys = new Set<string>()
  const listenersByKey = new Map<
    string,
    Set<(connected: boolean) => void>
  >()

  const update = (key: string, connected: boolean) => {
    const wasConnected = liveKeys.has(key)
    if (connected) {
      liveKeys.add(key)
    } else {
      liveKeys.delete(key)
    }
    if (wasConnected === connected) return
    for (const listener of listenersByKey.get(key) ?? []) {
      listener(connected)
    }
  }

  return {
    clear() {
      for (const key of Array.from(liveKeys)) update(key, false)
    },
    get(key: string) {
      return liveKeys.has(key)
    },
    reset() {
      liveKeys.clear()
      listenersByKey.clear()
    },
    subscribe(key: string, listener: (connected: boolean) => void) {
      let listeners = listenersByKey.get(key)
      if (!listeners) {
        listeners = new Set()
        listenersByKey.set(key, listeners)
      }
      listeners.add(listener)
      return () => {
        listeners?.delete(listener)
        if (listeners?.size === 0) listenersByKey.delete(key)
      }
    },
    update,
  }
}
